import {useCallback, useEffect, useRef, useState} from "react";
import { Environment } from "../../../enums/Environment";
import { kFakeTerms } from "../../../services/Faker";
import { isArrayNullUndefinedOrEmpty, isNullOrUndefined } from "../../../services/Validator";
import { kApiUnknownError, kEmptyResponseError } from "../../../constants/errors";
import { sendErrorToSentry } from "../../../services/Sentry";
import { get_terms } from "../../../api/term";
import SystemScreenError from "../../errors/SystemScreenError";
import SystemScreenLoader from "../../loaders/SystemScreenLoader";
import GlossaryLettersList from "../../lists/glossaryScreen/GlossaryLettersList";
import GlossaryTerms from "./GlossaryTerms";
import GlossaryGoToTopButton from "../../buttons/glossaryScreen/GlossaryGoToTopButton";
import GlossaryResearchVoid from "../../voids/glossaryScreen/GlossaryResearchVoid";
import {kSystemIcons} from "../../../constants/icons";
import GlossaryResearchClearButton from "../../buttons/glossaryScreen/GlossaryResearchClearButton";
import GlossaryResearchVerticalDivider from "../../dividers/glossaryScreen/GlossaryResearchVerticalDivider";
import GlossaryResearchModeButton from "../../buttons/glossaryScreen/GlossaryResearchModeButton";
import GlossarySearchLoader from "../../loaders/glossaryScreen/GlossarySearchLoader";

export default function GlossaryContent() {
    // Used in GlossaryScreen to display core content made of
    // research bar, letters list and terms list

    // PROPERTIES
    // ---- Meta
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // ---- functional
    const [terms, setTerms] = useState(null);
    const [filteredTerms, setFilteredTerms] = useState(null);
    // ---- search
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [isStrictMode, setIsStrictMode] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const refs = useRef({});
    const searchTimeout = useRef(null);
    const getTerms = useCallback(() => {
        // <<<< ---- FAKER ---- -----
        if (process.env.REACT_APP_ENV === Environment.FAKER) {
            setTerms(kFakeTerms)
            setIsLoading(false)
            setError(null)
            return
        }
        // ---- ---- FAKER ---- >>>>

        // Set loader on and clear potential error
        setIsLoading(true)
        setError(null)
        // Perform
        get_terms()
            .then((res) => {
                if (isNullOrUndefined(res) || isArrayNullUndefinedOrEmpty(res)) {
                    setError(kEmptyResponseError)
                    sendErrorToSentry(kEmptyResponseError.sentry, 'fetching Terms in GlossaryContent')
                } else {
                    setTerms(res)
                }
            })
            .catch((err) => {
                console.error(err)
                setError(kApiUnknownError)
                sendErrorToSentry(kApiUnknownError.sentry, 'fetching Terms in GlossaryContent')
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [])

    // HOOKS
    // ---- Triggered at rendering : set Terms
    useEffect(() => {
        getTerms()
    }, [getTerms])

    // ---- Triggered at rendering : scroll button
    useEffect(() => {
        const container = document.getElementById("glossary-layout");
        if (!container) return;

        const handleScroll = () => setShowScrollTop(container.scrollTop > 200);
        container.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => container.removeEventListener("scroll", handleScroll);
    }, []);

    // ---- Triggered at rendering : set filtered terms
    useEffect(() => {
        if (!terms) return;

        const query = debouncedSearch.trim().toLowerCase();
        if (query === "") {
            setFilteredTerms(null);
            return;
        }

        const newFiltered = {};
        Object.keys(terms).forEach((letter) => {
            const filteredEntries = terms[letter].filter((entry) => {
                const word = entry.word.toLowerCase();
                const definition = entry.definition?.toLowerCase() ?? "";
                return isStrictMode
                    ? word.startsWith(query)
                    : word.includes(query) || definition.includes(query);
            });
            if (filteredEntries.length > 0) {
                newFiltered[letter] = filteredEntries;
            }
        });

        setFilteredTerms(newFiltered);
    }, [debouncedSearch, isStrictMode, terms]);

    // ---- Triggered at rendering : searchTimeout
    useEffect(() => {
        return () => clearTimeout(searchTimeout.current);
    }, []);

    // BUILD
    if (!isNullOrUndefined(error))
        return <SystemScreenError error={error}
                                  retryCallback={() => getTerms()} />;
    if (isLoading)
        return <SystemScreenLoader />
    const displayedTerms = filteredTerms ?? terms;
    return (
        <div className="w-full flex flex-col justify-start items-center">
            <div className="bg-backgroundLight5 w-full h-[44px] flex items-center space-x-[8px] border-[2px] border-backgroundLight7 rounded-[8px] p-[4px]">
                <div className="select-none bg-backgroundLight6 w-[32px] h-[32px] flex justify-center items-center rounded-[4px]">
                    <i className="material-icons text-[20px] text-greyLight9">{kSystemIcons.search}</i>
                </div>
                <input className="bg-transparent flex-grow px-[4px] py-[2px] focus:outline-none"
                       type="text"
                       placeholder="Mot clé"
                       value={search}
                       onChange={(e) => handleSearchChange(e.target.value)} />
                {isSearching
                    ? <GlossarySearchLoader />
                    : <GlossaryResearchClearButton search={search}
                                                 onClear={() => handleSearchChange('')} /> }
                <GlossaryResearchVerticalDivider />
                <GlossaryResearchModeButton
                    isStrictMode={isStrictMode}
                    setIsStrictMode={setIsStrictMode}
                />
            </div>
            <GlossaryLettersList terms={displayedTerms}
                                 scrollToLetter={scrollToLetter} />
            {debouncedSearch.trim() !== "" && Object.keys(displayedTerms || {}).length === 0 && (
                <GlossaryResearchVoid />
            )}
            <GlossaryTerms refs={refs}
                           terms={displayedTerms}
                           highlight={highlight}
                           search={debouncedSearch} />
            {showScrollTop && <GlossaryGoToTopButton scrollToTop={scrollToTop} />}
        </div>
    );

    // METHODS
    // ---- UX : Highlight searched text
    function highlight(text, search) {
        const cleanSearch = search.trim();
        if (!cleanSearch) return text;
        const regex = new RegExp(`(${cleanSearch})`, "gi");
        return text.replace(regex, "<mark>$1</mark>");
    }

    // ---- UX : Scroll to letter
    function scrollToLetter(letter) {
        if (refs.current[letter]) {
            refs.current[letter].scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }

    // ---- UX : Scroll to top
    function scrollToTop() {
        const container = document.getElementById("glossary-layout");
        if (container) container.scrollTo({ top: 0, behavior: "smooth" });
    }

    // ---- LOGIC : Handle search change
    // function handleSearchChange(value) {
    //     setSearch(value);
    //     if (searchTimeout.current) clearTimeout(searchTimeout.current);
    //     searchTimeout.current = setTimeout(() => setDebouncedSearch(value), 400);
    // }
    function handleSearchChange(value) {
        setSearch(value);
        setIsSearching(true); // active le petit loader

        if (searchTimeout.current) clearTimeout(searchTimeout.current);

        searchTimeout.current = setTimeout(() => {
            setDebouncedSearch(value);
            setIsSearching(false); // désactive après le debounce
        }, 400);
    }

}
